/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */
/**
 * BOARDBAG SELECTOR V3 - JAVASCRIPT
 *
 * @version 3.0.0
 * @description Boardbag recommendation tool with slider UI, modal guide,
 *              lightbox, and Shopify cart integration.
 *
 * Dependencies (from Liquid):
 *   window.boardbagFamilies  - array of family metaobjects (dimensions, handle, etc.)
 *   window.boardbagStockData - array of product objects { variantId, title, handle, url,
 *                              image, price, priceFormatted, available, inventory,
 *                              innerLength, innerWidth, family, range }
 */

(function () {
  "use strict";

  var state = { length: 245, width: 70, hasSearched: false };

  var el = {};
  var guideA11yState = null;
  var lightboxA11yState = null;

  function cacheElements() {
    el.lengthSlider = document.getElementById("lengthSlider");
    el.lengthDisplay = document.getElementById("lengthDisplay");
    el.lengthDirect = document.getElementById("lengthDirect");
    el.lengthFill = document.getElementById("lengthFill");
    el.lengthStep = document.getElementById("lengthStep");
    el.lengthQuick = document.getElementById("lengthQuick");
    el.lengthTooltip = document.getElementById("lengthTooltip");
    el.lengthSliderZone = document.getElementById("lengthSliderZone");
    el.widthSlider = document.getElementById("widthSlider");
    el.widthDisplay = document.getElementById("widthDisplay");
    el.widthDirect = document.getElementById("widthDirect");
    el.widthFill = document.getElementById("widthFill");
    el.widthStep = document.getElementById("widthStep");
    el.widthQuick = document.getElementById("widthQuick");
    el.widthTooltip = document.getElementById("widthTooltip");
    el.widthSliderZone = document.getElementById("widthSliderZone");
    el.findBtn = document.getElementById("findBtn");
    el.resetBtn = document.getElementById("resetBtn");
    el.results = document.getElementById("results");
    el.resultsGrid = document.getElementById("resultsGrid");
    el.resultsSummary = document.getElementById("resultsSummary");
    el.placeholder = document.getElementById("placeholder");
    el.helpBtn = document.getElementById("helpBtn");
    el.guideModal = document.getElementById("guideModal");
    el.guideBackdrop = document.getElementById("guideBackdrop");
    el.guideClose = document.getElementById("guideClose");
    el.lightbox = document.getElementById("lightbox");
    el.lightboxImg = document.getElementById("lightboxImg");
  }

  /* ───────────────────────────────────────────────────────────
   * Initialization
   * ─────────────────────────────────────────────────────────── */

  function init() {
    cacheElements();
    if (!el.lengthSlider || !el.widthSlider) return;

    parseFamiliesData();

    bindSliders();
    bindQuickSelect();
    bindDirectInput();
    bindEditButtons();
    bindModal();
    bindLightbox();

    el.findBtn.addEventListener("click", findBoardbags);
    el.resetBtn.addEventListener("click", reset);

    updateFills();
    positionTooltip("length");
    positionTooltip("width");
  }

  /* ───────────────────────────────────────────────────────────
   * Tooltip positioning
   * ─────────────────────────────────────────────────────────── */

  function positionTooltip(dim) {
    var slider = el[dim + "Slider"];
    var tooltip = el[dim + "Tooltip"];
    if (!slider || !tooltip) return;
    var min = parseInt(slider.min, 10);
    var max = parseInt(slider.max, 10);
    var val = parseInt(slider.value, 10);
    var pct = ((val - min) / (max - min)) * 100;
    tooltip.textContent = val + " cm";
    tooltip.style.left = pct + "%";
  }

  /* ───────────────────────────────────────────────────────────
   * Sliders (input + drag state + touch)
   * ─────────────────────────────────────────────────────────── */

  function bindSliders() {
    ["length", "width"].forEach(function (dim) {
      var slider = el[dim + "Slider"];
      var zone = el[dim + "SliderZone"];

      slider.addEventListener("input", function () {
        state[dim] = parseInt(this.value, 10);
        updateDisplay(dim);
        positionTooltip(dim);
        if (state.hasSearched) findBoardbags();
      });

      slider.addEventListener("mousedown", function () {
        zone.classList.add("is-dragging");
      });
      slider.addEventListener(
        "touchstart",
        function () {
          zone.classList.add("is-dragging");
        },
        { passive: true },
      );
      document.addEventListener("mouseup", function () {
        zone.classList.remove("is-dragging");
      });
      document.addEventListener("touchend", function () {
        zone.classList.remove("is-dragging");
      });
    });
  }

  /* ───────────────────────────────────────────────────────────
   * Quick-select buttons
   * ─────────────────────────────────────────────────────────── */

  function bindQuickSelect() {
    el.lengthQuick.addEventListener("click", function (e) {
      if (e.target.classList.contains("dimension-input__quick-btn")) {
        state.length = parseInt(e.target.dataset.value, 10);
        el.lengthSlider.value = state.length;
        updateDisplay("length");
        if (state.hasSearched) findBoardbags();
      }
    });
    el.widthQuick.addEventListener("click", function (e) {
      if (e.target.classList.contains("dimension-input__quick-btn")) {
        state.width = parseInt(e.target.dataset.value, 10);
        el.widthSlider.value = state.width;
        updateDisplay("width");
        if (state.hasSearched) findBoardbags();
      }
    });
  }

  /* ───────────────────────────────────────────────────────────
   * Direct numeric input
   * ─────────────────────────────────────────────────────────── */

  function bindDirectInput() {
    ["length", "width"].forEach(function (dim) {
      var direct = el[dim + "Direct"];
      var slider = el[dim + "Slider"];
      var step = el[dim + "Step"];
      var min = parseInt(slider.min, 10);
      var max = parseInt(slider.max, 10);

      direct.addEventListener("change", function () {
        var val = Math.max(min, Math.min(max, parseInt(this.value, 10)));
        state[dim] = val;
        slider.value = val;
        this.value = val;
        updateDisplay(dim);
        step.classList.remove("is-editing");
        if (state.hasSearched) findBoardbags();
      });

      direct.addEventListener("keydown", function (e) {
        if (e.key === "Enter") this.blur();
      });
    });
  }

  /* ───────────────────────────────────────────────────────────
   * Edit buttons (toggle direct-input mode)
   * ─────────────────────────────────────────────────────────── */

  function bindEditButtons() {
    document.querySelectorAll(".dimension-input__edit").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var target = this.dataset.target;
        var step = el[target + "Step"];
        var direct = el[target + "Direct"];
        step.classList.toggle("is-editing");
        if (step.classList.contains("is-editing")) {
          direct.focus();
          direct.select();
        }
      });
    });
  }

  /* ───────────────────────────────────────────────────────────
   * Modal (How to Measure guide)
   * ─────────────────────────────────────────────────────────── */

  function bindModal() {
    if (!el.helpBtn || !el.guideModal) return;

    el.helpBtn.addEventListener("click", function () {
      el.guideModal.removeAttribute("hidden");
      el.guideModal.classList.add("is-open");
      el.guideModal.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";

      var a11y = window.UnifiberModalA11y;
      if (a11y) {
        guideA11yState = a11y.activate({
          roots: el.guideModal,
          trigger: el.helpBtn,
          focusTarget: el.guideClose,
        });
      } else if (el.guideClose) {
        el.guideClose.focus();
      }
    });
    el.guideClose.addEventListener("click", closeModal);
    el.guideBackdrop.addEventListener("click", closeModal);

    document.addEventListener("keydown", function (e) {
      // The image lightbox is a child layer in the guide flow. Let its
      // listener consume Escape first so one key press closes only the top
      // layer and returns focus to the visible guide control.
      if (
        e.key === "Escape" &&
        el.guideModal.classList.contains("is-open") &&
        !(el.lightbox && el.lightbox.classList.contains("is-open"))
      ) {
        closeModal();
      }
    });

    /* Tab switching inside modal */
    document.querySelectorAll(".guide-tab").forEach(function (tab, index) {
      var target = tab.dataset.tab;
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-selected", tab.classList.contains("is-active") ? "true" : "false");
      tab.tabIndex = tab.classList.contains("is-active") ? 0 : -1;
      if (target) {
        if (!tab.id) tab.id = "boardbag-guide-tab-" + target;
        tab.setAttribute("aria-controls", "tab-" + target);
        var linkedPanel = document.getElementById("tab-" + target);
        if (linkedPanel) {
          linkedPanel.setAttribute("role", "tabpanel");
          linkedPanel.setAttribute("aria-labelledby", tab.id);
          linkedPanel.hidden = !tab.classList.contains("is-active");
        }
      }

      tab.addEventListener("click", function () {
        var selectedTarget = this.dataset.tab;
        document.querySelectorAll(".guide-tab").forEach(function (t) {
          var isActive = t === tab;
          t.classList.toggle("is-active", isActive);
          t.setAttribute("aria-selected", isActive ? "true" : "false");
          t.tabIndex = isActive ? 0 : -1;
        });
        document.querySelectorAll(".guide-tab-content").forEach(function (c) {
          var isActive = c.id === "tab-" + selectedTarget;
          c.classList.toggle("is-active", isActive);
          c.hidden = !isActive;
        });
        var panel = document.getElementById("tab-" + selectedTarget);
        if (panel) {
          panel.classList.add("is-active");
          panel.hidden = false;
        }
      });

      tab.addEventListener("keydown", function (event) {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        var tabs = Array.from(document.querySelectorAll(".guide-tab"));
        var currentIndex = tabs.indexOf(tab);
        var nextIndex =
          event.key === "ArrowRight"
            ? (currentIndex + 1) % tabs.length
            : (currentIndex - 1 + tabs.length) % tabs.length;
        event.preventDefault();
        tabs[nextIndex].focus();
        tabs[nextIndex].click();
      });
    });
  }

  function closeModal() {
    // A lower layer must never close while the lightbox is active.
    if (el.lightbox && el.lightbox.classList.contains("is-open")) return;
    el.guideModal.classList.remove("is-open");
    el.guideModal.setAttribute("hidden", "");
    el.guideModal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";

    var a11y = window.UnifiberModalA11y;
    if (a11y && guideA11yState) {
      a11y.deactivate(guideA11yState);
      guideA11yState = null;
    }
  }

  /* ───────────────────────────────────────────────────────────
   * Lightbox for clickable images
   * ─────────────────────────────────────────────────────────── */

  function bindLightbox() {
    if (!el.lightbox || !el.lightboxImg) return;
    var lightboxClose = el.lightbox.querySelector(".lightbox__close");

    document.querySelectorAll(".guide-measurement__illustration, .guide-compare__card").forEach(function (item) {
      if (!item.hasAttribute("tabindex")) item.setAttribute("tabindex", "0");
      if (!item.hasAttribute("role")) item.setAttribute("role", "button");
      if (!item.hasAttribute("aria-label")) item.setAttribute("aria-label", "Enlarge image");

      item.addEventListener("keydown", function (event) {
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
          event.preventDefault();
          item.click();
        }
      });

      item.addEventListener("click", function () {
        var img = this.querySelector("img");
        if (!img) return;
        el.lightboxImg.src = img.src;
        el.lightboxImg.alt = img.alt;
        el.lightbox.removeAttribute("hidden");
        el.lightbox.classList.add("is-open");
        el.lightbox.setAttribute("aria-hidden", "false");
        document.body.style.overflow = "hidden";

        var a11y = window.UnifiberModalA11y;
        if (a11y) {
          lightboxA11yState = a11y.activate({
            roots: el.lightbox,
            trigger: this,
            focusTarget: lightboxClose,
          });
        } else if (lightboxClose) {
          lightboxClose.focus();
        }
      });
    });

    function closeLightbox() {
      el.lightbox.classList.remove("is-open");
      el.lightbox.setAttribute("hidden", "");
      el.lightbox.setAttribute("aria-hidden", "true");
      // Keep the page locked while the parent guide remains open.
      document.body.style.overflow = el.guideModal.classList.contains("is-open") ? "hidden" : "";

      var a11y = window.UnifiberModalA11y;
      if (a11y && lightboxA11yState) {
        a11y.deactivate(lightboxA11yState);
        lightboxA11yState = null;
      }
    }

    el.lightbox.addEventListener("click", function (e) {
      if (!e.target.closest(".lightbox__img") || e.target.closest(".lightbox__close")) {
        closeLightbox();
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && el.lightbox.classList.contains("is-open")) {
        closeLightbox();
      }
    });
  }

  /* ───────────────────────────────────────────────────────────
   * Display helpers
   * ─────────────────────────────────────────────────────────── */

  function updateDisplay(dim) {
    el[dim + "Display"].textContent = state[dim];
    el[dim + "Direct"].value = state[dim];
    updateQuickButtons(el[dim + "Quick"], state[dim]);
    updateFill(el[dim + "Slider"], el[dim + "Fill"]);
    positionTooltip(dim);
  }

  function updateQuickButtons(container, value) {
    container.querySelectorAll(".dimension-input__quick-btn").forEach(function (btn) {
      var isActive = parseInt(btn.dataset.value, 10) === value;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  function updateFill(slider, fill) {
    var min = parseInt(slider.min, 10);
    var max = parseInt(slider.max, 10);
    var pct = ((parseInt(slider.value, 10) - min) / (max - min)) * 100;
    fill.style.width = pct + "%";
  }

  function updateFills() {
    updateFill(el.lengthSlider, el.lengthFill);
    updateFill(el.widthSlider, el.widthFill);
  }

  /* ───────────────────────────────────────────────────────────
   * Find matching boardbags (from Liquid stock data)
   * ─────────────────────────────────────────────────────────── */

  function findBoardbags() {
    var l = state.length;
    var w = state.width;
    var products = Object.values(window.boardbagStockData || {});

    var matches = findMatchingBoardbags(l, w, products);

    state.hasSearched = true;
    el.findBtn.textContent = "Update Results";
    displayResults(matches, l, w);
  }

  /**
   * Find matching boardbags using family dimension ranges and product stock.
   * Returns products whose family range covers the board, scored by fit.
   */
  function findMatchingBoardbags(boardLength, boardWidth, products) {
    var families = window.boardbagFamilies || [];

    /* Build a set of family handles that fit the board */
    var fittingFamilies = {};
    families.forEach(function (family) {
      var lengthFits = boardLength >= family.inner_length_min && boardLength <= family.inner_length_max;
      var widthFits = boardWidth >= family.inner_width_min && boardWidth <= family.inner_width_max;
      if (lengthFits && widthFits) {
        fittingFamilies[family.handle] = family;
      }
    });

    /* Filter products that belong to a fitting family, then score */
    var matches = products
      .filter(function (p) {
        return p.innerLength >= boardLength && p.innerWidth >= boardWidth;
      })
      .map(function (p) {
        var excess = p.innerLength - boardLength + (p.innerWidth - boardWidth);
        return Object.assign({}, p, { fitScore: 100 - excess });
      })
      .sort(function (a, b) {
        return b.fitScore - a.fitScore;
      })
      .slice(0, 5);

    return matches;
  }

  /* ───────────────────────────────────────────────────────────
   * Display results
   * ─────────────────────────────────────────────────────────── */

  function displayResults(matches, length, width) {
    el.resultsSummary.textContent =
      "For your board (" +
      length +
      " \u00d7 " +
      width +
      " cm) \u2014 " +
      matches.length +
      " product" +
      (matches.length !== 1 ? "s" : "") +
      " found";

    while (el.resultsGrid.firstChild) {
      el.resultsGrid.removeChild(el.resultsGrid.firstChild);
    }

    if (matches.length === 0) {
      var empty = document.createElement("div");
      empty.className = "results__empty";

      var icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      icon.setAttribute("class", "results__empty-icon");
      icon.setAttribute("viewBox", "0 0 24 24");
      icon.setAttribute("fill", "none");
      icon.setAttribute("stroke", "currentColor");
      icon.setAttribute("stroke-width", "1.5");
      var path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z");
      icon.appendChild(path);
      empty.appendChild(icon);

      var p1 = document.createElement("p");
      p1.textContent = "No boardbags found for " + length + " \u00d7 " + width + " cm.";
      empty.appendChild(p1);

      var p2 = document.createElement("p");
      p2.textContent = "Try adjusting your dimensions or ";
      var link = document.createElement("a");
      link.href = "/collections/boardbags";
      link.textContent = "browse all boardbags";
      p2.appendChild(link);
      empty.appendChild(p2);

      el.resultsGrid.appendChild(empty);
    } else {
      matches.forEach(function (product, i) {
        el.resultsGrid.appendChild(createCard(product, i === 0));
      });
    }

    el.results.classList.add("is-visible");
    el.placeholder.style.display = "none";

    if (window.innerWidth < 900) {
      el.results.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  /* ───────────────────────────────────────────────────────────
   * Product card builder (uses real Shopify product data)
   * ─────────────────────────────────────────────────────────── */

  function createCard(product, isBestFit) {
    var card = document.createElement("div");
    card.className = "product-card" + (isBestFit ? " is-best-fit" : "");

    if (isBestFit) {
      var badge = document.createElement("span");
      badge.className = "product-card__badge";
      badge.textContent = "Best Fit";
      card.appendChild(badge);
    }

    var img = document.createElement("img");
    img.src = product.image;
    img.alt = product.title;
    img.className = "product-card__image";
    img.loading = "lazy";
    if (product.imageWidth) img.width = product.imageWidth;
    if (product.imageHeight) img.height = product.imageHeight;
    if (product.image) {
      img.srcset = product.image.replace(/width=\d+/, "width=400") + " 400w, " + product.image + " 600w";
      img.sizes = "(max-width: 600px) 400px, 600px";
    }
    card.appendChild(img);

    var content = document.createElement("div");
    content.className = "product-card__content";

    var range = document.createElement("span");
    range.className =
      "product-card__range product-card__range--" + (product.family || "").toLowerCase().replace(/\s+/g, "-");
    range.textContent = product.family;
    content.appendChild(range);

    var name = document.createElement("h3");
    name.className = "product-card__name";
    name.textContent = product.title;
    content.appendChild(name);

    var specs = document.createElement("p");
    specs.className = "product-card__specs";
    specs.textContent = "Inner: " + product.innerLength + " \u00d7 " + product.innerWidth + " cm";
    content.appendChild(specs);

    var meta = document.createElement("div");
    meta.className = "product-card__meta";

    var price = document.createElement("span");
    price.className = "product-card__price";
    price.textContent = product.priceFormatted || product.price;
    meta.appendChild(price);

    var stockClass = "in";
    var stockText = "In Stock";
    if (!product.available || product.inventory === 0) {
      stockClass = "out";
      stockText = "Out of Stock";
    } else if (product.inventory <= 5) {
      stockClass = "low";
      stockText = "Only " + product.inventory + " left";
    }

    var stock = document.createElement("span");
    stock.className = "product-card__stock product-card__stock--" + stockClass;
    var dot = document.createElement("span");
    dot.className = "product-card__stock-dot";
    stock.appendChild(dot);
    stock.appendChild(document.createTextNode(stockText));
    meta.appendChild(stock);

    content.appendChild(meta);
    card.appendChild(content);

    var actions = document.createElement("div");
    actions.className = "product-card__actions";

    if (product.available && product.inventory > 0 && product.variantId) {
      var atcBtn = document.createElement("button");
      atcBtn.className = "product-card__atc";
      atcBtn.type = "button";
      atcBtn.dataset.id = product.variantId;
      atcBtn.textContent = "Add to Cart";
      atcBtn.addEventListener("click", function () {
        window.boardbagAddToCart(product.variantId, 1, this);
      });
      actions.appendChild(atcBtn);
    }

    var viewLink = document.createElement("a");
    viewLink.href = product.url || "/products/" + product.handle;
    viewLink.className = "product-card__link";
    viewLink.textContent = "View Details";
    actions.appendChild(viewLink);

    card.appendChild(actions);
    return card;
  }

  /* ───────────────────────────────────────────────────────────
   * Reset
   * ─────────────────────────────────────────────────────────── */

  function reset() {
    state.length = 245;
    state.width = 70;
    state.hasSearched = false;
    el.lengthSlider.value = state.length;
    el.widthSlider.value = state.width;
    updateDisplay("length");
    updateDisplay("width");
    el.results.classList.remove("is-visible");
    el.placeholder.style.display = "";
    el.findBtn.textContent = "Find My Boardbag";
  }

  /* ───────────────────────────────────────────────────────────
   * Add to Cart (locale-aware Shopify AJAX API)
   * ─────────────────────────────────────────────────────────── */

  function addToCart(variantId, quantity, button) {
    if (!variantId) return;

    var prevText = button.textContent;
    button.textContent = "Adding...";
    button.disabled = true;

    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

    fetch(rootUrl + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error("Add to cart failed");
        return response.json();
      })
      .then(function () {
        button.textContent = "Added!";
        button.style.background = "#2e7d32";
        document.dispatchEvent(new CustomEvent("cart:updated"));
        setTimeout(function () {
          button.textContent = prevText;
          button.style.background = "";
          button.disabled = false;
        }, 2000);
      })
      .catch(function () {
        button.textContent = "Error";
        setTimeout(function () {
          button.textContent = prevText;
          button.disabled = false;
        }, 2000);
      });
  }

  /* ───────────────────────────────────────────────────────────
   * Public API
   * ─────────────────────────────────────────────────────────── */

  window.boardbagAddToCart = addToCart;

  function parseFamiliesData() {
    if (window.boardbagFamilies) return;
    var dataEl = document.getElementById("boardbagFamiliesData");
    if (dataEl) {
      try {
        window.boardbagFamilies = JSON.parse(dataEl.textContent);
      } catch (e) {
        window.boardbagFamilies = [];
      }
    }
  }

  window.initBoardbagSelector = function (families) {
    window.boardbagFamilies = families;
  };

  /* ───────────────────────────────────────────────────────────
   * Boot
   * ─────────────────────────────────────────────────────────── */

  document.addEventListener("DOMContentLoaded", init);
})();
